import { Controller } from "@hotwired/stimulus"

// Phase 2.11 Tier 1 — Inline confirmation modal for asset lifecycle
// buttons (Retire, Reopen, Rotate QR, Dismiss draft, Delete).
//
// The CLAUDE.md "Confirmation dialogs" rule forbids browser `confirm()`.
// This controller intercepts the form's submit event, shows a styled
// modal, and lets the user explicitly confirm — at which point the
// original form submits normally.
//
// Wiring: place `data-controller="asset-confirm"` on the form (or on a
// wrapping element that contains the form) and set
// `data-asset-confirm-message-value="..."` to override the default
// prompt. The controller intercepts the form's `submit` event the
// first time, shows the modal, and re-fires `requestSubmit()` after
// confirmation (with a flag to skip the second interception).
export default class extends Controller {
  static values = {
    message: { type: String, default: "Are you sure?" },
    confirmLabel: { type: String, default: "Confirm" }
  }

  connect() {
    this._onSubmit = this._onSubmit.bind(this)
    this._form = this.element.tagName === "FORM"
      ? this.element
      : this.element.querySelector("form")
    if (this._form) {
      this._form.addEventListener("submit", this._onSubmit)
    }
  }

  disconnect() {
    if (this._form) {
      this._form.removeEventListener("submit", this._onSubmit)
    }
    if (this._modal) this._modal.remove()
  }

  _onSubmit(event) {
    if (this._confirmed) return
    event.preventDefault()
    this._showModal()
  }

  _showModal() {
    const modal = document.createElement("div")
    modal.className = "fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
    modal.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
        <div class="px-5 py-4 border-b border-gray-200">
          <h3 class="text-sm font-semibold text-gray-900">Confirm action</h3>
        </div>
        <div class="px-5 py-4 text-[13px] text-gray-700">${this._escape(this.messageValue)}</div>
        <div class="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <button type="button" data-role="cancel"
                  class="px-3 py-1.5 text-[13px] font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg">Cancel</button>
          <button type="button" data-role="confirm"
                  class="px-3 py-1.5 text-[13px] font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg">${this._escape(this.confirmLabelValue)}</button>
        </div>
      </div>
    `
    modal.addEventListener("click", (e) => {
      if (e.target === modal) this._closeModal()
    })
    modal.querySelector("[data-role='cancel']").addEventListener("click", () => this._closeModal())
    modal.querySelector("[data-role='confirm']").addEventListener("click", () => {
      this._closeModal()
      this._confirmed = true
      this._form.requestSubmit()
    })
    document.body.appendChild(modal)
    this._modal = modal
  }

  _closeModal() {
    if (this._modal) {
      this._modal.remove()
      this._modal = null
    }
  }

  _escape(s) {
    const div = document.createElement("div")
    div.textContent = s
    return div.innerHTML
  }
}
